import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Plus } from "lucide-react";
import { Alert, AlertDescription, AlertTitle, Badge, Button, Modal, ProgressBar, Skeleton, cn } from "@fox1ck-org/ui";
import type { TrackingDomain } from "@gen/tracker/v1/tracking_domain_pb";

import { ConnectDomainDrawer } from "@/components/domains/connect-domain-drawer";
import { DomainDrawer } from "@/components/domains/domain-drawer";
import { PlanLimitView, useMeterUsage } from "@/components/plan/plan-limit";
import { domainState, stateLabelKey } from "@/lib/domain-binding";
import { useT, type TKey } from "@/lib/i18n";
import { formatInt } from "@/lib/format";
import { useCanWrite } from "@/lib/permissions";
import { getSourceType, kindFromProto } from "@/lib/source-types";
import { useListAffiliateNetworks } from "@/lib/services/network-service";
import { useListLanders } from "@/lib/services/lander-service";
import { useListOffers } from "@/lib/services/offer-service";
import { useGetTrackingDomain, useListTrackingDomains } from "@/lib/services/tracking-domain-service";
import { useListTrafficSources } from "@/lib/services/traffic-source-service";
import { useReportTimezone } from "@/lib/services/workspace-service";

interface Step {
  key: string;
  title: TKey;
  /** What is there, once it is. */
  done?: string;
  /** What to do, while it is not. */
  todo: string;
  action: TKey;
  to: string;
  /** The step's own buttons while it is not done, instead of the one link. */
  actions?: ReactNode;
  loading: boolean;
  /** The count could not be read: neither a tick nor a cross would be true. */
  failed: boolean;
  count: number | undefined;
}

/**
 * «Готово к первой кампании» (доска TodayFirstRun; card S11b — the first
 * steps of an empty org): the domain the ad link lives on, the four things a
 * launch is made of, then the first campaign — ticked by the list RPCs' own
 * totals, never by a «seen the tour» flag: a checklist that lies about what
 * exists is worse than none. The first step not done is the one the primary
 * button points at. Mounted only on a workspace with no campaign, so the
 * queries do not fire otherwise (our own org never sees it).
 *
 * The domain is the org's first step and has two ways in: «Свой домен» — the
 * fqdn, then the drawer with the records the domain provider asks for (a
 * CNAME; nothing here names a host) — and «Купить домен», which says where a
 * domain comes from and leads to the same connect. The plan's domain limit
 * stands under it when the plan has one.
 */
export function ReadinessChecklist() {
  const t = useT();
  const canWrite = useCanWrite();
  const tz = useReportTimezone();
  const networks = useListAffiliateNetworks({ limit: 1 });
  const sources = useListTrafficSources({ limit: 1 });
  const offers = useListOffers({ limit: 1 });
  const landers = useListLanders({ limit: 1 });
  const domains = useListTrackingDomains({ limit: 1 });
  const domainLimit = useMeterUsage("tracker.domains");
  const [connecting, setConnecting] = useState(false);
  const [buying, setBuying] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = useGetTrackingDomain(openId ?? undefined);

  const net = networks.data?.items[0];
  const src = sources.data?.items[0];
  const clickId = src ? getSourceType(kindFromProto(src.kind)).clickIdParam : "";
  const dom: TrackingDomain | undefined = domains.data?.items[0];

  const steps: Step[] = [
    {
      key: "domain",
      title: "onb.domain",
      done: dom ? t("onb.domain.done", { fqdn: dom.fqdn, state: t(stateLabelKey(domainState(dom))).toLowerCase() }) : undefined,
      todo: canWrite ? t("onb.domain.todo") : t("onb.readOnly"),
      action: "onb.domain.own",
      to: "/domains",
      actions: canWrite ? (
        <>
          <Button variant="secondary" size="sm" onClick={() => setBuying(true)}>
            {t("onb.domain.buy")}
          </Button>
          <Button variant="primary" size="sm" onClick={() => setConnecting(true)}>
            <Plus className="size-3.5" /> {t("onb.domain.own")}
          </Button>
        </>
      ) : null,
      loading: domains.isPending,
      failed: domains.isError,
      count: domains.data?.total,
    },
    {
      key: "network",
      title: "today.checklist.network",
      done: net ? t("ops.first.network.done", { name: net.name }) : undefined,
      todo: t("ops.first.network.todo"),
      action: "ops.first.network.add",
      to: "/library/networks",
      loading: networks.isPending,
      failed: networks.isError,
      count: networks.data?.total,
    },
    {
      key: "source",
      title: "ops.first.source",
      done: src ? (clickId ? t("ops.first.source.done", { name: src.name, param: clickId }) : src.name) : undefined,
      todo: t("ops.first.source.todo"),
      action: "ops.first.source.add",
      to: "/library/sources",
      loading: sources.isPending,
      failed: sources.isError,
      count: sources.data?.total,
    },
    {
      key: "offer",
      title: "today.checklist.offer",
      done: t("ops.first.counted", { count: formatInt(offers.data?.total ?? 0) }),
      todo: t("ops.first.offer.todo"),
      action: "ops.first.offer.add",
      to: "/library/offers",
      loading: offers.isPending,
      failed: offers.isError,
      count: offers.data?.total,
    },
    {
      key: "lander",
      title: "today.checklist.lander",
      done: t("ops.first.counted", { count: formatInt(landers.data?.total ?? 0) }),
      todo: t("ops.first.lander.todo"),
      action: "ops.first.lander.add",
      to: "/library/landers",
      loading: landers.isPending,
      failed: landers.isError,
      count: landers.data?.total,
    },
    {
      // Mounted only while there is no campaign: never done here.
      key: "campaign",
      title: "onb.campaign",
      todo: t("onb.campaign.todo"),
      action: "onb.campaign.create",
      to: "/campaigns/new",
      actions: canWrite ? undefined : null,
      loading: false,
      failed: false,
      count: 0,
    },
  ];
  const isDone = (s: Step) => !s.failed && (s.count ?? 0) > 0;
  const doneCount = steps.filter(isDone).length;
  const next = steps.find((s) => !s.loading && !s.failed && !isDone(s));

  return (
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card" aria-label={t("ops.first.title")}>
      <header className="flex h-11 shrink-0 items-center gap-2 px-3.5">
        <h2 className="text-section-title">{t("ops.first.title")}</h2>
        <Badge variant="primary" className="ml-auto">
          {t("ops.first.progress", { done: doneCount, total: steps.length })}
        </Badge>
      </header>
      <ProgressBar value={doneCount} max={steps.length} size="xs" tone="primary" showPercent={false} className="shrink-0" />
      <ol className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {steps.map((s, i) => {
          const done = isDone(s);
          const current = s === next;
          return (
            <li key={s.key} className={cn("flex flex-col gap-2 border-b border-border px-3.5 py-3 last:border-b-0", current && "bg-primary-soft")} data-testid={`first-step-${s.key}`}>
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs",
                    done ? "border-ok-line bg-ok-soft text-ok" : "border-border-strong text-muted-foreground",
                  )}
                >
                  {done ? <Check className="size-3.5" strokeWidth={2.5} /> : i + 1}
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-medium">{t(s.title)}</span>
                  {s.loading ? <Skeleton className="mt-1 h-3 w-48" /> : <span className="text-xs text-muted-foreground">{s.failed ? "—" : done ? s.done : s.todo}</span>}
                </div>
                {s.loading || s.failed ? null : done ? (
                  <Button asChild variant="ghost" size="sm">
                    <Link to={s.to}>{t("ops.open")}</Link>
                  </Button>
                ) : s.actions !== undefined ? (
                  <div className="flex shrink-0 items-center gap-2">{s.actions}</div>
                ) : (
                  <Button asChild variant={current ? "primary" : "secondary"} size="sm">
                    <Link to={s.to}>{t(s.action)}</Link>
                  </Button>
                )}
              </div>
              {s.key === "domain" ? <PlanLimitView usage={domainLimit} className="ml-9" /> : null}
            </li>
          );
        })}
      </ol>
      <Modal
        open={buying}
        onClose={() => setBuying(false)}
        title={t("onb.buy.title")}
        size="md"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setBuying(false);
              setConnecting(true);
            }}
          >
            {t("onb.buy.connect")}
          </Button>
        }
      >
        <p className="text-sm text-muted-foreground">{t("onb.buy.body")}</p>
      </Modal>
      <ConnectDomainDrawer
        open={connecting}
        onOpenChange={setConnecting}
        onCreated={(d) => {
          setConnecting(false);
          setOpenId(d.id);
        }}
      />
      <DomainDrawer domain={openId ? opened.data : undefined} tz={tz} onOpenChange={(o) => !o && setOpenId(null)} />
    </section>
  );
}

/**
 * «Как идёт человек» (доска TodayFirstRun): the road a click takes, in the
 * product's words, and that spend from Facebook arrives on its own.
 */
export function HowItWorks() {
  const t = useT();
  const road: TKey[] = ["ops.first.road.ad", "ops.first.road.domain", "ops.first.road.lander", "ops.first.road.offer", "ops.first.road.network"];
  return (
    <section className="flex w-124 shrink-0 flex-col gap-3 self-start rounded-lg border border-border bg-card pb-3.5" aria-label={t("ops.first.road")}>
      <header className="flex h-11 items-center border-b border-border px-3.5">
        <h2 className="text-section-title">{t("ops.first.road")}</h2>
      </header>
      <div className="flex flex-wrap items-center gap-2 px-3.5">
        {road.map((k, i) => (
          <span key={k} className="flex items-center gap-2">
            <Badge variant="outline" size="md">
              {t(k)}
            </Badge>
            {i < road.length - 1 ? <ArrowRight aria-hidden className="size-3.5 text-muted-foreground" strokeWidth={1.5} /> : null}
          </span>
        ))}
      </div>
      <p className="px-3.5 text-sm text-muted-foreground">{t("ops.first.road.body")}</p>
      <Alert variant="info" className="mx-3.5 w-auto">
        <Check className="size-4" />
        <div className="min-w-0">
          <AlertTitle>{t("ops.first.spend")}</AlertTitle>
          <AlertDescription>{t("ops.first.spend.body")}</AlertDescription>
        </div>
      </Alert>
    </section>
  );
}
